// Ported from web src/interfaces/olmps.ts — keep in sync until packages/domain exists.

export type Sector = 'S' | 'H' | 'A' | 'P' | 'E';
export const SECTORS: Sector[] = ['S', 'H', 'A', 'P', 'E'];
export const SECTOR_LABELS: Record<Sector, string> = {
  S: 'Social',
  H: 'Health',
  A: 'Admin',
  P: 'Personal',
  E: 'Economic',
};

export type Stage = 'S' | 'T' | 'A' | 'G' | 'E';
export const STAGES: Stage[] = ['S', 'T', 'A', 'G', 'E'];
export const STAGE_LABELS: Record<Stage, string> = {
  S: 'Step',
  T: 'Today',
  A: 'Aim',
  G: 'Goal',
  E: 'Endeavour',
};
export const STAGE_SHORT: Record<Stage, string> = {
  S: 'Step',
  T: 'Today',
  A: 'Aim',
  G: 'Goal',
  E: 'End',
};
export const STAGE_HORIZON: Record<Stage, string> = {
  S: 'Moment',
  T: 'Day',
  A: 'Week',
  G: 'Month',
  E: 'Quarter',
};

export type Gear = 'orient' | 'regulate' | 'direct' | 'execute' | 'revise';
export const GEARS: Gear[] = ['orient', 'regulate', 'direct', 'execute', 'revise'];
export const GEAR_META: Record<Gear, { letter: string; name: string; question: string }> = {
  orient: { letter: 'O', name: 'Orient', question: 'Where am I going?' },
  regulate: { letter: 'R', name: 'Regulate', question: 'What resources do I need?' },
  direct: { letter: 'D', name: 'Direct', question: 'How will I get there?' },
  execute: { letter: 'E', name: 'Execute', question: 'Getting there.' },
  revise: { letter: 'R', name: 'Revise', question: 'What changed?' },
};

export type CellKey = `${Sector}:${Stage}:${Gear}`;
export function cellKey(sector: Sector, stage: Stage, gear: Gear): CellKey {
  return `${sector}:${stage}:${gear}`;
}
export function parseCellKey(key: CellKey): { sector: Sector; stage: Stage; gear: Gear } {
  const [sector, stage, gear] = key.split(':') as [Sector, Stage, Gear];
  return { sector, stage, gear };
}

export interface Assignment {
  toolIds: string[];
}
export type Calibration = Partial<Record<CellKey, Assignment>>;

// Flat ORDER ring (spec 2026-07-21): every STAGE uses the same locked gear→tool
// mapping — the DEMI starting five: Orient=Oracle, Regulate=Numa, Direct=Kyrs,
// Execute=Metis, Revise=Clio. Supersedes the earlier per-stage template.
// NOTE (new-system conformance): each gear's full 3-tier roster (DEMI / TYTN / OLMPN
// — internal ids mortal/titan/olympian — e.g. execute = metis/themis/janus, orient = oracle/
// telos/astra) lives in ../olmps/ascension.ts (GEAR_TIERS) — the single
// shared source. This ring only picks the ONE default tool shown per
// Stage×Gear dashboard cell; it does not duplicate the roster.
const ORDER_RING: Record<Gear, string> = {
  orient: 'oracle',
  regulate: 'numa',
  direct: 'kyrs',
  execute: 'metis',
  revise: 'clio',
};
export const DEFAULT_TEMPLATE_BY_STAGE: Record<Stage, Record<Gear, string>> = {
  E: { ...ORDER_RING },
  G: { ...ORDER_RING },
  A: { ...ORDER_RING },
  T: { ...ORDER_RING },
  S: { ...ORDER_RING },
};

export function defaultAssignmentFor(_sector: Sector, stage: Stage, gear: Gear): Assignment {
  return { toolIds: [DEFAULT_TEMPLATE_BY_STAGE[stage][gear]] };
}

/** Auto-picks a sector for a project going active without one, so it always
 *  lands on an ASTRA spoke instead of vanishing (ASTRA only plots active
 *  projects that have a sector). Fills the least-loaded sector first, tie
 *  broken by SECTORS order (S=Social=12 o'clock, clockwise) — spreads new
 *  projects across empty spokes before doubling up in one wedge. Pass the
 *  sectors of every other currently-active, sector-tagged project. */
export function pickAutoSector(activeSectors: (Sector | null | undefined)[]): Sector {
  const counts: Record<Sector, number> = { S: 0, H: 0, A: 0, P: 0, E: 0 };
  for (const s of activeSectors) if (s && s in counts) counts[s]++;
  return SECTORS.reduce((best, s) => (counts[s] < counts[best] ? s : best), SECTORS[0]);
}

export const TOOL_NAMES: Record<string, string> = {
  clio: 'CLIO',
  epic: 'EPIC',
  kyrs: 'KYRS',
  krnos: 'KRNOS',
  metis: 'METIS',
  telos: 'TELOS',
  moyri: 'MoyRi',
  jnus: 'JANUS',
  tkne: 'TKNE',
  atlas: 'ATLAS',
  poros: 'POROS',
  oracle: 'ATLAS', // ORACLE retired (18 Oct 2026): its readout is ATLAS
  astra: 'ASTRA',
  argos: 'ARGOS',
  themis: 'THEMIS',
  numa: 'NUMA',
  nemos: 'NEMOS',
  sofia: 'SOFIA',
  hrmez: 'HRMEZ',
  akeso: 'AKESO',
  astrs: 'ASTRS',
  plutus: 'PLUTUS',
  hygia: 'HYGIA',
  hypnos: 'HYPNOS',
  syqi: 'SyQi',
};
export const ALL_TOOL_IDS = Object.keys(TOOL_NAMES);
